/**
 * DateGridCard — date pairs around the trip's dates, priced as a round trip (register
 * P6). Depart dates run down, return dates across, each cell a price per person.
 * Owner's page only: the API answers 404 to anyone else, and then this renders nothing.
 * It never changes the trip's dates. That stays the traveler's own action on the flight.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tripsApi } from "@/lib/api";
import { getAirlineName as fullAirlineName } from "@/lib/airlines";
import type { DateGrid, DateGridCell } from "@/lib/types";

/** "American", the way a person says it. */
const getAirlineName = (code: string) => fullAirlineName(code).replace(/\s+(Airlines|Air Lines)$/, "");

const FLEX_OPTIONS = [
  { value: 0, label: "Exact" },
  { value: 1, label: "1 day" },
  { value: 2, label: "2 days" },
  { value: 3, label: "3 days" },
];

const parse = (ymd: string) => new Date(`${ymd}T00:00:00Z`);
/** "Thu, Dec 10" */
export const dayLong = (ymd: string) =>
  parse(ymd).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
/** "Dec 10" */
const dayShort = (ymd: string) => parse(ymd).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const dayOnly = (ymd: string) => String(parse(ymd).getUTCDate());
const sameMonth = (a: string, b: string) => a.slice(0, 7) === b.slice(0, 7);
const usd = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

/** "3 hours ago" */
export function agoText(iso: string, now: Date = new Date()): string {
  const minutes = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 60_000));
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** The one sentence a tapped cell gives. */
export function cellSentence(cell: DateGridCell, filterCarrier: string | null, now: Date = new Date(), nonstopOnly = false): string {
  const pair = `${dayLong(cell.departDate)} to ${dayLong(cell.returnDate)}`;
  const ago = cell.checkedAt ? agoText(cell.checkedAt, now) : null;
  switch (cell.state) {
    case "ok":
      return `${pair}: ${usd(cell.price ?? 0)} round trip each${cell.carrier ? ` on ${getAirlineName(cell.carrier)}` : ""}, ${nonstopOnly ? "nonstop" : "may include connections"}, checked ${ago}.`;
    case "no_result":
      return `${pair}: we could not read a ${nonstopOnly ? "nonstop " : ""}round trip price${filterCarrier ? ` on ${getAirlineName(filterCarrier)}` : ""}, checked ${ago}. That does not mean none is sold. We will try again at the next price check.`;
    case "failed":
      return `${pair}: our last check did not finish. We will try again at the next price check.`;
    case "refused":
      return `${pair}: not checked. ${cell.refusedReason ?? ""}`.trim();
    default:
      return `${pair}: not checked yet. We check a few pairs at each price check.`;
  }
}

/** "You hold Dec 10 to 15." Null when nothing is held or booked. */
export function holdLine(hold: Extract<DateGrid, { applicable: true }>["hold"]): string | null {
  if (!hold) return null;
  const verb = hold.status === "held" ? "hold" : "booked";
  if (hold.departDate && hold.returnDate) {
    const end = sameMonth(hold.departDate, hold.returnDate) ? dayOnly(hold.returnDate) : dayShort(hold.returnDate);
    return `You ${verb} ${dayShort(hold.departDate)} to ${end}.`;
  }
  if (hold.departDate) return `You ${verb} your flight out on ${dayShort(hold.departDate)}.`;
  if (hold.returnDate) return `You ${verb} your flight home on ${dayShort(hold.returnDate)}.`;
  return null;
}

function cellText(cell: DateGridCell, nonstopOnly: boolean): string {
  if (cell.state === "ok" && cell.price != null) return usd(cell.price);
  if (cell.state === "refused") return "–";
  if (cell.state === "no_result") return "Not read";
  return "Not yet";
}

const key = (d: string, r: string) => `${d}|${r}`;

export function DateGridView({
  grid,
  onFlex,
  saving,
  saveFailed,
  now,
}: {
  grid: Extract<DateGrid, { applicable: true }>;
  onFlex?: (flex: number) => void;
  saving?: boolean;
  saveFailed?: boolean;
  now?: Date;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const cellOf = new Map(grid.cells.map((c) => [key(c.departDate, c.returnDate), c]));
  const pickedCell = picked ? cellOf.get(picked) ?? null : null;
  const current = key(grid.currentPair.departDate, grid.currentPair.returnDate);
  const cheapest = grid.cheapestPair ? key(grid.cheapestPair.departDate, grid.cheapestPair.returnDate) : null;
  const anyChecked = grid.cells.some((c) => c.checkedAt != null);
  const hold = holdLine(grid.hold);
  const airline = grid.filterCarrier ? getAirlineName(grid.filterCarrier) : null;
  const nonstop = grid.nonstopOnly;

  return (
    <section className="rounded-xl border bg-card p-4 shadow-paper" aria-label="Your dates, priced as a round trip">
      <h3 className="text-sm font-medium text-foreground">Your dates, priced as a round trip</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {nonstop
          ? `Cheapest nonstop round trip on ${airline ?? "any airline"}, at any time of day. Your own flights can cost more. Prices are per person.`
          : `Cheapest round trip on ${airline ?? "any airline"}. Prices are per person.`}
      </p>
      {!nonstop && <p className="mt-1 text-xs font-medium text-foreground">These prices may include connections.</p>}
      {hold && <p className="mt-1 text-xs font-medium text-foreground">{hold}</p>}

      {!anyChecked && (
        <p className="mt-3 text-sm text-muted-foreground">We have not checked these dates yet. Prices fill in over the next day.</p>
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="border-separate border-spacing-1 text-xs tabular-nums">
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-card px-1 pb-1 text-left align-bottom font-normal text-muted-foreground">
                Leave
                <span className="block whitespace-nowrap">Return</span>
              </th>
              {grid.columns.map((r) => (
                <th key={r} scope="col" className="min-w-[52px] px-1 pb-1 text-center align-bottom font-medium text-foreground">
                  {dayShort(r)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((d) => (
              <tr key={d}>
                <th scope="row" className="sticky left-0 z-10 whitespace-nowrap bg-card pr-1 text-left font-medium text-foreground">
                  {dayShort(d)}
                </th>
                {grid.columns.map((r) => {
                  const k = key(d, r);
                  const cell = cellOf.get(k);
                  if (!cell) return <td key={r} />;
                  const isCurrent = k === current;
                  const isCheapest = k === cheapest;
                  const muted = cell.state !== "ok" || cell.stale;
                  return (
                    <td key={r} className="p-0">
                      <button
                        type="button"
                        aria-pressed={picked === k}
                        aria-label={cellSentence(cell, grid.filterCarrier, now, nonstop)}
                        onClick={() => setPicked(picked === k ? null : k)}
                        className={[
                          "flex min-h-[3.25rem] w-full min-w-[52px] flex-col items-center justify-center rounded-md border px-1 leading-tight",
                          isCurrent ? "border-2 border-foreground" : "border-border",
                          picked === k ? "bg-muted" : "bg-background",
                          muted ? "text-muted-foreground" : "font-medium text-foreground",
                          cell.state === "ok" ? "" : "text-[11px] font-normal",
                        ].join(" ")}
                      >
                        <span>{cellText(cell, nonstop)}</span>
                        {cell.state === "ok" && <span className="text-[10px] font-normal text-muted-foreground">{nonstop ? "nonstop" : "may connect"}</span>}
                        {isCheapest && <span className="text-[10px] font-medium text-teal-700">Lowest</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 min-h-[1.25rem] text-sm text-foreground" aria-live="polite">
        {pickedCell ? cellSentence(pickedCell, grid.filterCarrier, now, nonstop) : <span className="text-xs text-muted-foreground">Outlined is your dates. Tap any price for when we checked it.</span>}
      </p>

      {grid.roundTripTotal != null && grid.sumOfOneWays != null && (
        <p className="mt-2 text-sm text-foreground">
          Bought together {usd(grid.roundTripTotal)} each. Two one-ways would be {usd(grid.sumOfOneWays)}.
        </p>
      )}

      <div className="mt-3">
        <p id="date-grid-flex" className="text-xs font-medium text-foreground">
          How flexible
        </p>
        <div role="radiogroup" aria-labelledby="date-grid-flex" className="mt-1 grid grid-cols-4 gap-1">
          {FLEX_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={grid.flex === o.value}
              disabled={saving}
              onClick={() => grid.flex !== o.value && onFlex?.(o.value)}
              className={`h-11 rounded-md border text-xs font-medium disabled:opacity-60 ${
                grid.flex === o.value ? "border-foreground bg-foreground text-background" : "border-border bg-background text-foreground"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {saveFailed && <p className="mt-1 text-xs text-amber-700">That did not save. Try again.</p>}
        <p className="mt-1 text-xs text-muted-foreground">This only changes which dates we price, either side of yours. Your trip's dates stay as they are.</p>
      </div>
    </section>
  );
}

export default function DateGridCard({ tripId }: { tripId: string }) {
  const client = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dateGrid", tripId],
    queryFn: () => tripsApi.dateGrid(tripId),
    retry: false,
  });
  const flex = useMutation({
    mutationFn: (value: number) => tripsApi.setDateFlex(tripId, value),
    onSuccess: () => client.invalidateQueries({ queryKey: ["dateGrid", tripId] }),
  });

  if (isLoading) {
    return (
      <section className="rounded-xl border bg-card p-4 shadow-paper">
        <h3 className="text-sm font-medium text-foreground">Your dates, priced as a round trip</h3>
        <p className="mt-1 text-xs text-muted-foreground">Loading your date prices&hellip;</p>
      </section>
    );
  }
  if (isError) {
    // Not the owner: this card is not for them.
    if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
    return (
      <section className="rounded-xl border bg-card p-4 shadow-paper">
        <h3 className="text-sm font-medium text-foreground">Your dates, priced as a round trip</h3>
        <p className="mt-1 text-xs text-muted-foreground">We could not load these prices just now.</p>
      </section>
    );
  }
  if (!data || !data.applicable) return null;
  return <DateGridView grid={data} onFlex={(v) => flex.mutate(v)} saving={flex.isPending} saveFailed={flex.isError} />;
}
